import salaryService from 'ApiService/Accountant/salaryService';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const EmployeeSalaryDetails = ({ employeeData }) => {
  const [employeeSalaryDetails, setEmployeeSalaryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const { state } = useContext(AuthContext);
  useEffect(() => {
    if (employeeData) {
      fetchEmployeeSalaryDetails();
    }
  }, [employeeData]);

  const fetchEmployeeSalaryDetails = async () => {
    try {
      const selectedBranch = state?.selectedBranch || '';

      setLoading(true);
      const response = await salaryService.getEmployeeSalaryDetails({
        employeeId: employeeData.employeeId,
        year: new Date().getFullYear(),
        selectedBranch,
      });
      if (response.data.success) {
        setEmployeeSalaryDetails(response.data.data);
      } else {
        toast.error('Failed to fetch salary details.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch salary details.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex  items-center justify-center">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <h1 className="text-2xl font-bold text-navy-900 dark:text-white">
        Employee Salary Details
      </h1>

      {employeeSalaryDetails ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {/* Employee Information */}
          <div className="rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
            <div className="border-b pb-4">
              <p className="text-sm text-navy-700 dark:text-navy-100">
                Employee Information
              </p>
              <p className="text-lg font-semibold text-navy-900 dark:text-white">
                {employeeSalaryDetails?.employeeId?.name || 'No Name Available'}
              </p>
            </div>
            <div className="mt-4 space-y-2">
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Employee Type
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.employeeType === 'Account'
                    ? 'Accountant'
                    : employeeSalaryDetails?.employeeType === 'Placement'
                    ? 'Placement Officer'
                    : employeeSalaryDetails?.employeeType}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Current Salary
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  ₹
                  {employeeSalaryDetails?.currentSalary?.toLocaleString() ||
                    '0'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Account Number
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.bankDetails?.accountNumber || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  IFSC Code
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.bankDetails?.ifscCode || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Bank Name
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.bankDetails?.bankName || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Salary History */}
          <div className="col-span-full rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800 ">
            <h2 className="mb-4 text-lg font-semibold text-navy-900 dark:text-white">
              Salary History
            </h2>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 text-sm font-medium text-navy-700 dark:text-navy-100">
                      Date
                    </th>
                    <th className="pb-2 text-sm font-medium text-navy-700 dark:text-navy-100">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {employeeSalaryDetails?.salaryHistory.map((record, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="py-2 text-sm text-navy-900 dark:text-navy-300">
                        {new Date(record.date).toLocaleDateString()}
                      </td>
                      <td className="py-2 text-sm font-medium text-navy-900 dark:text-navy-300">
                        ₹{record.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Salary Records */}
          <div className="col-span-full rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
            <h2 className="mb-4 text-lg font-semibold text-navy-900 dark:text-white">
              Monthly Salary Records
            </h2>
            <div className="max-h-64 overflow-y-auto">
              {employeeSalaryDetails?.monthlySalaryRecords?.length > 0 ? (
                employeeSalaryDetails.monthlySalaryRecords.map((yearRecord) => (
                  <div key={yearRecord.year}>
                    <h3 className="mb-2 font-semibold text-navy-900 dark:text-white">
                      Year: {yearRecord.year}
                    </h3>
                    {yearRecord.monthlyData.map((monthData) => (
                      <div
                        key={monthData._id}
                        className="mb-4 rounded-lg border p-4 dark:bg-navy-700"
                      >
                        <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                          Month: {monthData.month}
                        </p>
                        <p className="text-sm text-navy-900 dark:text-white">
                          Amount: ₹{monthData.amount.toLocaleString()}
                        </p>
                        <p className="text-sm text-navy-900 dark:text-white">
                          Bonus: ₹{monthData.bonus.toLocaleString()}
                        </p>
                        <p className="text-sm text-navy-900 dark:text-white">
                          Deductions: ₹{monthData.deductions.toLocaleString()}
                        </p>
                        <p className="text-sm text-navy-900 dark:text-white">
                          Net Amount: ₹{monthData.netAmount.toLocaleString()}
                        </p>
                        <p className="text-sm text-navy-900 dark:text-white">
                          Paid: {monthData.isPaid ? 'Yes' : 'No'}
                        </p>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <p className="text-center text-sm text-navy-700 dark:text-navy-100">
                  No monthly salary records found.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-white p-6 text-center shadow-sm dark:bg-navy-800">
          <p className="text-navy-700 dark:text-navy-100">
            No salary details found for this employee.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeeSalaryDetails;
